// Flavour category catalogue. Each entry maps to one of the FlavorCategory
// values used on products (Mint, Fruity, Citrus, Berry, Tropical, Other)
// and drives the /flavour/:slug pages: rendering, SSR meta, and sitemap.

export interface FlavourInfo {
  /** URL slug used in /flavour/:slug. Lowercase. */
  slug: string;
  /** Matches the `category` field on products. */
  name: string;
  /** Short marketing line under the H1. */
  tagline: string;
  /** ~100 word intro paragraph rendered on the category page. */
  description: string;
  seoTitle: string;
  seoDescription: string;
}

export const flavours: FlavourInfo[] = [
  {
    slug: "mint",
    name: "Mint",
    tagline: "Crisp, clean, and refreshing",
    description:
      "Mint is the most popular flavour profile for nicotine pouches — and for good reason. From cool spearmint to icy peppermint, mint pouches deliver a fresh, clean experience that pairs well with any time of day. We stock the full mint range from ZYN, VELO, Pablo and White Fox, in strengths from gentle 6mg to extra-strong 17mg.",
    seoTitle: "Mint Nicotine Pouches in India | ZYN, VELO, White Fox | ZeroSmoke",
    seoDescription:
      "Shop mint nicotine pouches in India — peppermint, spearmint, cool mint and frost variants from ZYN, VELO, Pablo, White Fox. Tobacco-free, all strengths.",
  },
  {
    slug: "fruity",
    name: "Fruity",
    tagline: "Sweet, bold, and unmistakable",
    description:
      "Fruity nicotine pouches go beyond mint with bold, sweet flavours that range from grape and blackcurrant to mango and passionfruit. Perfect when you want something different from the standard mint experience. Top picks include VELO Icy Berries, Pablo Grape Ice, and ZYN Black Cherry — all tobacco-free, all delivered across India.",
    seoTitle: "Fruity Nicotine Pouches in India | Berry, Grape, Tropical | ZeroSmoke",
    seoDescription:
      "Buy fruity nicotine pouches online in India — berry, grape, mango, blackcurrant and more. Tobacco-free flavours from ZYN, VELO, Pablo, White Fox.",
  },
  {
    slug: "citrus",
    name: "Citrus",
    tagline: "Bright, zesty, and energising",
    description:
      "Citrus nicotine pouches blend lemon, lime, orange and grapefruit notes for a bright, energising experience. ZYN Citrus and VELO Lime Flame are popular picks. Citrus flavours pair well with afternoon work sessions and outdoor activities — clean, sharp, and never overpowering.",
    seoTitle: "Citrus Nicotine Pouches in India | Lemon, Lime, Orange | ZeroSmoke",
    seoDescription:
      "Shop citrus nicotine pouches in India — lemon, lime, orange and grapefruit flavours. Tobacco-free, smoke-free, fast delivery across India.",
  },
  {
    slug: "berry",
    name: "Berry",
    tagline: "Juicy, ice-cold, and balanced",
    description:
      "Berry nicotine pouches feature blueberry, blackcurrant, strawberry and cherry — often combined with mint or ice for a balanced finish. VELO Icy Berries and ZYN Red Fruits are bestsellers. Berry flavours are an easy gateway for users moving from mint to fruitier profiles.",
    seoTitle: "Berry Nicotine Pouches in India | Blueberry, Cherry, Blackcurrant | ZeroSmoke",
    seoDescription:
      "Berry nicotine pouches in India — blueberry, blackcurrant, strawberry, cherry. Tobacco-free icy-berry blends from VELO, ZYN, Pablo. Delivered nationwide.",
  },
  {
    slug: "tropical",
    name: "Tropical",
    tagline: "Sun-soaked, vibrant, and bold",
    description:
      "Tropical nicotine pouches bring mango, pineapple, passionfruit and tropical-punch flavours to the category. VELO Tropical Mango and Pablo Tropical Punch lead this profile. Tropical variants are typically icy-finished — a sweet front, cool back — and pair well with active days and warm weather.",
    seoTitle: "Tropical Nicotine Pouches in India | Mango, Pineapple, Passionfruit | ZeroSmoke",
    seoDescription:
      "Tropical nicotine pouches in India — mango, pineapple, passionfruit and tropical-punch flavours. Tobacco-free, smoke-free. Order at ZeroSmoke.in",
  },
  {
    slug: "other",
    name: "Other",
    tagline: "Coffee, cola, liquorice and more",
    description:
      "Beyond the standard mint, fruit and citrus profiles, you'll find adventurous variants: ZYN Coffee, Pablo Cola, salty liquorice, even cinnamon. These pouches are for users who already know what they like and want to expand the palette. All tobacco-free, all authentic.",
    seoTitle: "Specialty Nicotine Pouch Flavours in India | Coffee, Cola, Liquorice | ZeroSmoke",
    seoDescription:
      "Specialty nicotine pouches in India — coffee, cola, liquorice and unique flavours from ZYN, VELO, Pablo. Tobacco-free, delivered nationwide.",
  },
];

export const flavourBySlug: Record<string, FlavourInfo> = Object.fromEntries(
  flavours.map((f) => [f.slug, f])
);

export const flavourByName: Record<string, FlavourInfo> = Object.fromEntries(
  flavours.map((f) => [f.name, f])
);
